import { useLocalization } from '@lib/localization';
import {
  CheckCircle,
  ChevronRight,
  Description,
  Edit,
  Error as ErrorIcon,
  ExpandMore,
  Folder,
  Insights,
  Refresh,
} from '@mui/icons-material';
import {
  Box,
  Checkbox,
  CircularProgress,
  Collapse,
  IconButton,
  TextField,
  Typography,
} from '@mui/material';
import { memo, useCallback, useMemo, useState } from 'react';
import { type BulkTreeItem } from '../types';

// TreeItem renders a single row of the bulk-upload tree (a folder, project or
// inspection) and, for folders, recursively renders its children inside a
// collapsible section. A row shows: a tri-state checkbox, an expand chevron
// (folders only), a type icon, the name (editable in place for folders), and a
// right-aligned status indicator (spinner / success / skipped / error + retry).

// ───────────────────────────────────────────────────────────────────────────
// Visuals — icon and color per item type
// ───────────────────────────────────────────────────────────────────────────

const ITEM_ICONS = {
  folder: Folder,
  project: Description,
  inspection: Insights,
} as const;

const ITEM_ICON_COLORS = {
  folder: '#facc15', // amber
  project: '#60a5fa', // blue
  inspection: '#a78bfa', // violet
} as const;

// ───────────────────────────────────────────────────────────────────────────
// Tri-state selection
// ───────────────────────────────────────────────────────────────────────────

interface SelectionState {
  checked: boolean;
  indeterminate: boolean;
}

// A folder's checkbox reflects its whole subtree: fully checked when every
// descendant is selected, indeterminate when only some are, unchecked when none
// are. Leaf items (project/inspection) just mirror their own `selected` flag.
const computeSelectionState = (item: BulkTreeItem): SelectionState => {
  if (item.type !== 'folder') {
    return { checked: item.selected, indeterminate: false };
  }

  let totalCount = 1;
  let selectedCount = item.selected ? 1 : 0;

  const visitDescendants = (folder: BulkTreeItem) => {
    for (const child of folder.children) {
      totalCount++;
      if (child.selected) selectedCount++;
      visitDescendants(child);
    }
  };

  visitDescendants(item);

  if (selectedCount === 0) return { checked: false, indeterminate: false };
  if (selectedCount === totalCount) return { checked: true, indeterminate: false };
  return { checked: false, indeterminate: true };
};

// ───────────────────────────────────────────────────────────────────────────
// Component
// ───────────────────────────────────────────────────────────────────────────

interface TreeItemProps {
  item: BulkTreeItem;
  depth: number;
  expandedIds: Set<string>;
  uploadInProgress: boolean;
  retryingItemId: string | null;
  onToggleExpand: (itemId: string) => void;
  onToggleSelection: (itemId: string) => void;
  onRetry: (item: BulkTreeItem) => void;
  onRename: (itemId: string, name: string) => void;
}

const TreeItem = memo<TreeItemProps>(({
  item,
  depth,
  expandedIds,
  uploadInProgress,
  retryingItemId,
  onToggleExpand,
  onToggleSelection,
  onRetry,
  onRename,
}) => {
  const { t } = useLocalization();

  // ── Derived display values ────────────────────────────────────────────────
  const isFolder = item.type === 'folder';
  const isExpanded = expandedIds.has(item.id);
  const isRetrying = retryingItemId === item.id;

  const Icon = ITEM_ICONS[item.type];
  const iconColor = ITEM_ICON_COLORS[item.type];

  const selection = useMemo(() => computeSelectionState(item), [item]);

  // ── In-place folder rename ────────────────────────────────────────────────
  const [isEditing, setIsEditing] = useState(false);
  const [draftName, setDraftName] = useState(item.name);

  const startEditing = useCallback((event: React.MouseEvent) => {
    event.stopPropagation();
    setDraftName(item.name);
    setIsEditing(true);
  }, [item.name]);

  // Commit on Enter or blur; ignore empty/unchanged names. The parent decides
  // whether this is a local-only rename or one that needs backend confirmation.
  const commitRename = useCallback(() => {
    setIsEditing(false);
    const nextName = draftName.trim();
    if (nextName && nextName !== item.name) onRename(item.id, nextName);
  }, [draftName, item.id, item.name, onRename]);

  const handleNameKeyDown = useCallback((event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      commitRename();
    } else if (event.key === 'Escape') {
      event.preventDefault();
      setIsEditing(false);
    }
  }, [commitRename]);

  // ── Row interactions ──────────────────────────────────────────────────────
  // Clicking a folder row toggles its expansion (but not while renaming it).
  const handleRowClick = useCallback(() => {
    if (isFolder && !isEditing) onToggleExpand(item.id);
  }, [isFolder, isEditing, item.id, onToggleExpand]);

  // The checkbox and retry button sit inside the clickable row, so they stop
  // propagation to avoid also toggling the folder's expansion.
  const handleCheckboxClick = useCallback((event: React.MouseEvent) => {
    event.stopPropagation();
    onToggleSelection(item.id);
  }, [item.id, onToggleSelection]);

  const handleRetryClick = useCallback((event: React.MouseEvent) => {
    event.stopPropagation();
    onRetry(item);
  }, [item, onRetry]);

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <Box>
      <Box
        onClick={handleRowClick}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          paddingY: 0.5,
          paddingLeft: depth * 2, // indent one step per tree level
          cursor: isFolder ? 'pointer' : 'default',
          opacity: selection.checked || selection.indeterminate ? 1 : 0.5, // dim deselected rows
          '&:hover': isFolder ? { backgroundColor: 'action.hover' } : undefined,
        }}
      >
        <Checkbox
          size="small"
          checked={selection.checked}
          indeterminate={selection.indeterminate}
          onClick={handleCheckboxClick}
          disabled={uploadInProgress}
          sx={{ padding: 0.5 }}
        />

        {/* Expand/collapse chevron for folders; a spacer keeps leaves aligned. */}
        {isFolder
          ? (isExpanded ? <ExpandMore fontSize="small" /> : <ChevronRight fontSize="small" />)
          : <Box sx={{ width: 20 }} />}

        <Icon sx={{ fontSize: 18, color: iconColor }} />

        {/* Name — an editable text field while renaming, plain text otherwise. */}
        {isEditing ? (
          <TextField
            value={draftName}
            onChange={event => setDraftName(event.target.value)}
            onKeyDown={handleNameKeyDown}
            onBlur={commitRename}
            onClick={event => event.stopPropagation()}
            size="small"
            variant="standard"
            autoFocus
            sx={{ '& .MuiInputBase-input': { paddingY: 0, fontSize: '0.875rem' } }}
          />
        ) : (
          <Typography
            variant="body2"
            sx={
              isFolder && !item.selected
                ? { textDecoration: 'line-through', color: 'text.secondary' }
                : undefined
            }
          >
            {item.name}
          </Typography>
        )}

        {/* Rename affordance — folders only, and not during an upload. */}
        {isFolder && !uploadInProgress && !isEditing && (
          <IconButton size="small" onClick={startEditing} title={t('Project_Bulk_RenameFolder')} sx={{ padding: 0.25 }}>
            <Edit sx={{ fontSize: 14 }} />
          </IconButton>
        )}

        {/* An unselected folder isn't created on the backend — its selected
            contents are still uploaded, into the nearest selected parent. The
            aggregate (tri-state) checkbox can't convey this on its own, so call
            it out explicitly. */}
        {isFolder && !item.selected && !isEditing && (
          <Typography
            variant="caption"
            sx={{ color: 'warning.main', fontStyle: 'italic', whiteSpace: 'nowrap' }}
          >
            {t('Project_Bulk_WontBeCreated')}
          </Typography>
        )}

        <Box flexGrow={1} />

        {/* Right-aligned status indicator. */}
        {item.status === 'uploading' && <CircularProgress size={14} />}
        {item.status === 'success' && <CheckCircle sx={{ fontSize: 16, color: '#22c55e' }} />}
        {item.status === 'skipped' && (
          <Typography variant="caption" color="text.secondary">{t('Project_Bulk_Skipped')}</Typography>
        )}
        {item.status === 'error' && (
          <Box display="flex" alignItems="center" gap={0.5}>
            <ErrorIcon sx={{ fontSize: 16, color: '#ef4444' }} />
            {item.error && (
              <Typography
                variant="caption"
                color="error"
                noWrap
                title={item.error} // full message on hover when truncated
                sx={{ maxWidth: 240 }}
              >
                {item.error}
              </Typography>
            )}
            <IconButton
              size="small"
              onClick={handleRetryClick}
              disabled={uploadInProgress || isRetrying}
              title={t('Project_Bulk_Retry')}
              sx={{ ml: 0.5 }}
            >
              {isRetrying ? <CircularProgress size={14} /> : <Refresh sx={{ fontSize: 16 }} />}
            </IconButton>
          </Box>
        )}
      </Box>

      {/* Children, rendered recursively inside a collapsible section. */}
      {isFolder && (
        <Collapse in={isExpanded} unmountOnExit>
          {item.children.map(child => (
            <TreeItem
              key={child.id}
              item={child}
              depth={depth + 1}
              expandedIds={expandedIds}
              uploadInProgress={uploadInProgress}
              retryingItemId={retryingItemId}
              onToggleExpand={onToggleExpand}
              onToggleSelection={onToggleSelection}
              onRetry={onRetry}
              onRename={onRename}
            />
          ))}
        </Collapse>
      )}
    </Box>
  );
});

export default TreeItem;
